import {
  beautify,
  NoWrap,
  numberFormat,
  returnObject,
} from "../../../../utils/helpers";

type TParkedCarsAdhocProps = {
  dateFilterKey?: string;
  status?: string;
  noWrapColumns?: string[];
  numberColumns?: string[];
};

const useParkedCarsAdhoc = ({
  noWrapColumns = [],
  numberColumns = [],
}: TParkedCarsAdhocProps) => {
  const tableColumns = (data: any) => {
    const firstItem = data[0] || {};

    // Define the predefined column keys to avoid duplicates
    const predefinedColumns = [
      "car_number",
      "parking_name",
      "slot_name",
      "car_size",
      "is_parking_ended",
      "hours_spent",
      "total_charge",
    ];

    // Define columns to exclude from dynamic generation
    const excludedColumns = [
      "latitude",
      "longitude",
      "start_time",
      "end_time",
      ...predefinedColumns,
    ];

    // Custom columns for parked cars data with specific filters
    const filterColumns = [
      {
        id: "car_number",
        header: <NoWrap>Car Number</NoWrap>,
        filter: { type: "text" },
      },
      {
        id: "parking_name",
        header: <NoWrap>Parking Location</NoWrap>,
        filter: { type: "text" },
      },
      {
        id: "slot_name",
        header: <NoWrap>Slot</NoWrap>,
        filter: { type: "text" },
      },
      {
        id: "car_size",
        header: <NoWrap>Car Size</NoWrap>,
        filter: {
          type: "select",
          options: [
            { value: "Small", label: "Small" },
            { value: "Big", label: "Big" },
            { value: "Medium", label: "Medium" },
          ],
        },
      },
      {
        id: "is_parking_ended",
        header: <NoWrap>Status</NoWrap>,
        filter: {
          type: "select",
          options: [
            { value: true, label: "Completed" },
            { value: false, label: "Active" },
          ],
        },
      },
      {
        id: "hours_spent",
        header: <NoWrap>Hours</NoWrap>,
        filter: { type: "number" },
      },
      {
        id: "total_charge",
        header: <NoWrap>Amount (UGX)</NoWrap>,
        filter: { type: "number" },
      },
    ];

    // Generate columns from data structure, excluding predefined and excluded ones
    const dynamicColumns = Object.keys(firstItem)
      .filter((column) => !excludedColumns.includes(column))
      .map((column: any) => ({
        id: column,
        header: <NoWrap>{beautify(column)}</NoWrap>,
        filter: true,
      }));

    return [...filterColumns, ...dynamicColumns];
  };

  const tableData = (data: any) => {
    return data?.map((item: any) => ({
      ...item,
      car_number: (
        <NoWrap className="font-mono font-semibold">{item.car_number}</NoWrap>
      ),
      parking_name: <NoWrap>{item.parking_name}</NoWrap>,
      slot_name: (
        <span className="px-2 py-1 bg-blue-50 text-blue-800 rounded text-xs font-medium">
          {item.slot_name}
        </span>
      ),
      car_size: item.car_size ? (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            item.car_size === "Small"
              ? "bg-green-100 text-green-800"
              : item.car_size === "Big"
                ? "bg-red-100 text-red-800"
                : "bg-yellow-100 text-yellow-800"
          }`}
        >
          {item.car_size}
        </span>
      ) : (
        <span className="text-gray-400 text-xs">N/A</span>
      ),
      is_parking_ended: (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            item.is_parking_ended
              ? "bg-gray-100 text-gray-800"
              : "bg-green-100 text-green-800"
          }`}
        >
          {item.is_parking_ended ? "Completed" : "Active"}
        </span>
      ),
      hours_spent: (
        <span className="font-medium">
          {item.hours_spent} {item.hours_spent === 1 ? "hr" : "hrs"}
        </span>
      ),
      total_charge:
        item.total_charge > 0 ? (
          <span className="font-medium text-green-600">
            UGX {numberFormat(item.total_charge)}
          </span>
        ) : (
          <span className="text-gray-400">Free</span>
        ),
      phone_number: <NoWrap className="font-mono">{item.phone_number}</NoWrap>,
      email: item.email ? (
        <NoWrap className="text-blue-600">{item.email}</NoWrap>
      ) : (
        <span className="text-gray-400 text-xs">No email</span>
      ),
      start_time: (
        <NoWrap className="text-xs text-gray-600">
          {new Date(item.start_time).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </NoWrap>
      ),
      end_time: item.end_time ? (
        <NoWrap className="text-xs text-gray-600">
          {new Date(item.end_time).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </NoWrap>
      ) : (
        <span className="text-orange-600 text-xs font-medium">Ongoing</span>
      ),
      ...returnObject(
        noWrapColumns.length > 0,
        noWrapColumns.reduce((obj, column) => {
          if (item[column] !== undefined) {
            obj[column] = <NoWrap>{item[column]}</NoWrap>;
          }
          return obj;
        }, {}),
      ),
      ...returnObject(
        numberColumns.length > 0,
        numberColumns.reduce((obj, column) => {
          if (item[column] !== undefined && item[column] !== null) {
            obj[column] = numberFormat(item[column]);
          }
          return obj;
        }, {}),
      ),
    }));
  };

  const initialFilters = {
    car_number: "",
    parking_name: "",
    slot_name: "",
    car_size: "",
    is_parking_ended: "",
  };

  // Additional utility functions for parked cars analysis
  const getParkedCarsStats = (data: any) => {
    const total = data.length;
    const active = data.filter((item: any) => !item.is_parking_ended).length;
    const completed = data.filter((item: any) => item.is_parking_ended).length;

    const uniqueCars = new Set(data.map((item: any) => item.car_number)).size;
    const uniquePhones = new Set(data.map((item: any) => item.phone_number))
      .size;

    const totalHours = data.reduce(
      (sum: number, item: any) => sum + (item.hours_spent || 0),
      0,
    );

    const totalRevenue = data.reduce(
      (sum: number, item: any) => sum + (item.total_charge || 0),
      0,
    );

    const averageSessionTime = total > 0 ? (totalHours / total).toFixed(1) : 0;
    const averageChargePerHour =
      totalHours > 0 ? (totalRevenue / totalHours).toFixed(0) : 0;

    return {
      total,
      active,
      completed,
      uniqueCars,
      uniquePhones,
      totalHours,
      totalRevenue,
      averageSessionTime,
      averageChargePerHour,
      completionRate: total > 0 ? ((completed / total) * 100).toFixed(1) : 0,
    };
  };

  const getParkedCarsLocationSummary = (data: any) => {
    const locationGroups = data.reduce((acc: any, item: any) => {
      const location = item.parking_name;
      if (!acc[location]) {
        acc[location] = {
          total: 0,
          active: 0,
          completed: 0,
          totalHours: 0,
          totalRevenue: 0,
          uniqueCars: new Set(),
        };
      }
      acc[location].total++;
      if (item.is_parking_ended) {
        acc[location].completed++;
      } else {
        acc[location].active++;
      }
      acc[location].totalHours += item.hours_spent || 0;
      acc[location].totalRevenue += item.total_charge || 0;
      acc[location].uniqueCars.add(item.car_number);
      return acc;
    }, {});

    return Object.entries(locationGroups).map(
      ([location, stats]: [string, any]) => ({
        parking_name: location,
        total: stats.total,
        active: stats.active,
        completed: stats.completed,
        totalHours: stats.totalHours,
        totalRevenue: stats.totalRevenue,
        uniqueCars: stats.uniqueCars.size,
        averageHours: (stats.totalHours / stats.total).toFixed(1),
        revenuePerSession: (stats.totalRevenue / stats.total).toFixed(0),
      }),
    );
  };

  const getCarSizeSummary = (data: any) => {
    const sizeGroups = data.reduce((acc: any, item: any) => {
      const size = item.car_size || "Unknown";
      if (!acc[size]) {
        acc[size] = {
          total: 0,
          totalHours: 0,
          totalRevenue: 0,
        };
      }
      acc[size].total++;
      acc[size].totalHours += item.hours_spent || 0;
      acc[size].totalRevenue += item.total_charge || 0;
      return acc;
    }, {});

    return Object.entries(sizeGroups).map(([size, stats]: [string, any]) => ({
      car_size: size,
      total: stats.total,
      totalHours: stats.totalHours,
      totalRevenue: stats.totalRevenue,
      averageHours: (stats.totalHours / stats.total).toFixed(1),
      averageCharge: (stats.totalRevenue / stats.total).toFixed(0),
    }));
  };

  const getCustomerSummary = (data: any) => {
    const customerGroups = data.reduce((acc: any, item: any) => {
      const phone = item.phone_number;
      if (!acc[phone]) {
        acc[phone] = {
          phone_number: phone,
          email: item.email,
          sessions: 0,
          cars: new Set(),
          totalHours: 0,
          totalSpent: 0,
          locations: new Set(),
        };
      }
      acc[phone].sessions++;
      acc[phone].cars.add(item.car_number);
      acc[phone].totalHours += item.hours_spent || 0;
      acc[phone].totalSpent += item.total_charge || 0;
      acc[phone].locations.add(item.parking_name);
      return acc;
    }, {});

    return Object.values(customerGroups).map((customer: any) => ({
      ...customer,
      cars: Array.from(customer.cars),
      locations: Array.from(customer.locations),
      uniqueCars: customer.cars.size,
      uniqueLocations: customer.locations.size,
      averageSpentPerSession: (customer.totalSpent / customer.sessions).toFixed(
        0,
      ),
      averageHoursPerSession: (customer.totalHours / customer.sessions).toFixed(
        1,
      ),
    }));
  };

  return {
    tableColumns,
    tableData,
    initialFilters,
    getParkedCarsStats,
    getParkedCarsLocationSummary,
    getCarSizeSummary,
    getCustomerSummary,
  };
};

export default useParkedCarsAdhoc;

// Specific implementation for parked cars reports
export const useParkedCarsAdhocReport = () => {
  const noWrapColumns = [
    "car_number",
    "phone_number",
    "email",
    "parking_name",
    "slot_name",
  ];
  const numberColumns = ["hours_spent", "total_charge"];

  const {
    tableColumns,
    tableData,
    initialFilters,
    getParkedCarsStats,
    getParkedCarsLocationSummary,
    getCarSizeSummary,
    getCustomerSummary,
  } = useParkedCarsAdhoc({
    numberColumns,
    noWrapColumns,
  });

  return {
    tableColumns,
    tableData,
    initialFilters,
    getParkedCarsStats,
    getParkedCarsLocationSummary,
    getCarSizeSummary,
    getCustomerSummary,
  };
};
